// @mui
import { styled } from '@mui/material/styles';
import { AppBar, Box, Stack, Toolbar } from '@mui/material';
// hooks
import useOffSetTop from '@common/hooks/useOffSetTop';
import useResponsive from '@common/hooks/useResponsive';
// utils
import cssStyles from '@common/utils/cssStyles';
// config
import { HEADER, NAVBAR } from '@common/config';
// components
import Logo from '@common/components/Logo';
import Iconify from '@common/components/Iconify';
import { IconButtonAnimate } from '@common/components/animate';
//
import AccountPopover from './AccountPopover';
// import useAuth from '@common/hooks/useAuth';
// import { LevelDisplayName, Role } from '@common/@types/auth';
import WalletStatus from './WalletStatus';
import { CurrentViewType } from 'common/analytics/amplitude';

// ----------------------------------------------------------------------

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
  currentView?: CurrentViewType;
  bgColor?: string;
};

/* const LEVELS_USING_ALPHAS = [
  LevelDisplayName.L2,
  LevelDisplayName.L3,
  LevelDisplayName.L1,
  LevelDisplayName.Multilevel,
]; */
export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
  currentView,
  bgColor,
}: Props) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  // const { user } = useAuth();
  /* const shouldSeeAlphaWallet =
    LEVELS_USING_ALPHAS.filter(
      (level) =>
        user?.alphaAuthAttributes?.levels.map((l) => l.displayName).includes(level) &&
        user?.alphaAuthAttributes?.campusId !== '3' &&
        user?.alphaAuthAttributes?.campusId !== '4' // Wallet should NOT be visible for Ukrainian kids campus ID 4
    ).length > 0 || user?.alphaAuthAttributes?.role === Role.ADMIN; */
  const shouldSeeAlphaWallet = false; // temporarily hide the wallet for all users until it is updated to support 2hr learning

  const isDesktop = useResponsive('up', 'lg');

  return (
    <RootStyle
      style={bgColor ? { backgroundColor: bgColor } : {}}
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
    >
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        {/* <Searchbar /> */}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {shouldSeeAlphaWallet && <WalletStatus />}
          <AccountPopover currentView={currentView} />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
